import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { VerifyBanner } from "../components/VerifyBanner";
import { AccountDeletion } from "../components/AccountDeletion";
import { Stars } from "../components/Ratings";
import { Clock, CheckCircle2, XCircle, PenLine, ShieldAlert, Trash2, EyeOff } from "lucide-react";

const STATUS = {
  pending: { icon: Clock, label: "Pending review", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  live: { icon: CheckCircle2, label: "Live", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { icon: XCircle, label: "Rejected", cls: "bg-red-50 text-red-700 border-red-200" },
  removed: { icon: EyeOff, label: "Removed", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  hidden: { icon: EyeOff, label: "Hidden", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

export default function TenantDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState({ title: "", body: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = () => {
    api.get("/reviews/mine").then((r) => setReviews(r.data)).catch(() => {});
    api.get("/disputes/mine").then((r) => setDisputes(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    if (user.role !== "tenant") { navigate("/"); return; }
    load();
  }, [user, loading, navigate]);

  const confirmDispute = async (id) => { await api.post(`/disputes/${id}/confirm`); load(); };
  const openDisputes = disputes.filter((d) => d.status === "open");

  const startEdit = (r) => {
    setError("");
    setEditId(r.id);
    setEdit({ title: r.title || "", body: r.body || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const saveEdit = async (id) => {
    if (!edit.body.trim()) { setError("Please write your review before resubmitting."); return; }
    setBusy(true); setError("");
    try {
      await api.post(`/reviews/${id}/resubmit`, { title: edit.title, body: edit.body });
      setEditId(null); load();
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Delete this review permanently? This cannot be undone.")) return;
    try { await api.delete(`/reviews/${id}`); load(); }
    catch (err) { alert(formatApiError(err.response?.data?.detail) || err.message); }
  };

  return (
    <div className="App bg-[#F4F7FA] min-h-screen">
      <Navbar />
      <VerifyBanner />
      <div className="max-w-5xl mx-auto px-5 py-12">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] font-mono text-mint-dark">Tenant portal</span>
            <h1 className="mt-1 text-3xl font-bold font-heading text-navy">My reviews</h1>
          </div>
          <Link to="/write-review" data-testid="tenant-write-review"
            className="flex items-center gap-2 bg-mint text-white font-semibold px-5 py-2.5 rounded-xl hover:bg-mint-dark transition-colors">
            <PenLine className="w-4 h-4" /> New review
          </Link>
        </div>

        {openDisputes.length > 0 && (
          <div className="mt-8 bg-amber-50 border border-amber-200 rounded-2xl p-6" data-testid="tenant-disputes">
            <h2 className="font-heading font-bold text-lg text-amber-900 flex items-center gap-2"><ShieldAlert className="w-5 h-5" /> A landlord has disputed your review</h2>
            <p className="text-sm text-amber-800 mt-1">Within 14 days, confirm you stand by your review, or edit it in light of the landlord's reason and resubmit it for our team to re-check. Otherwise it will be hidden.</p>
            <div className="mt-4 flex flex-col gap-3">
              {openDisputes.map((d) => (
                <div key={d.id} className="bg-white rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`tenant-dispute-${d.id}`}>
                  <div>
                    <p className="font-semibold text-navy">{d.full_address}</p>
                    <p className="text-sm text-slate-500">Landlord's reason: {d.reason || "—"}</p>
                    <p className="text-xs text-slate-400 font-mono mt-1">Respond by {new Date(d.deadline).toLocaleDateString("en-GB")}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {reviews.some((r) => r.id === d.review_id) && (
                      <button data-testid={`edit-dispute-${d.id}`} onClick={() => startEdit(reviews.find((r) => r.id === d.review_id))}
                        className="border border-slate-300 text-slate-700 font-semibold px-4 py-2 rounded-xl hover:bg-slate-50 transition-colors text-sm">Edit &amp; resubmit</button>
                    )}
                    <button data-testid={`confirm-dispute-${d.id}`} onClick={() => confirmDispute(d.id)}
                      className="bg-mint text-white font-semibold px-4 py-2 rounded-xl hover:bg-mint-dark transition-colors text-sm">I stand by my review</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {error && <p className="mt-4 text-sm text-red-600" data-testid="tenant-error">{error}</p>}

        <div className="mt-8 flex flex-col gap-4" data-testid="tenant-reviews">
          {reviews.length === 0 && (
            <div className="border border-dashed border-slate-300 rounded-md p-12 text-center text-slate-500" data-testid="tenant-empty">
              You haven't written any reviews yet.
            </div>
          )}
          {reviews.map((r) => {
            const s = STATUS[r.status] || STATUS.pending;
            return (
              <div key={r.id} className="border border-slate-200 rounded-md p-6 bg-white" data-testid={`tenant-review-${r.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <Link to={`/address/${r.property_slug}`} className="font-semibold text-slate-900 hover:text-brand">{r.property_slug.replace(/-/g, " ")}</Link>
                    <div className="mt-1 flex items-center gap-2"><Stars value={r.overall} /><span className="font-mono text-sm text-slate-500">{r.overall.toFixed(1)}</span></div>
                  </div>
                  <span className={`flex items-center gap-1.5 px-3 py-1 text-xs font-semibold border rounded-md ${s.cls}`} data-testid={`status-${r.id}`}>
                    <s.icon className="w-3.5 h-3.5" /> {s.label}
                  </span>
                </div>

                {editId === r.id ? (
                  <div className="mt-4 flex flex-col gap-2" data-testid={`edit-box-${r.id}`}>
                    <input data-testid={`edit-title-${r.id}`} value={edit.title} placeholder="Review title"
                      onChange={(e) => setEdit({ ...edit, title: e.target.value })}
                      className="border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-mint" />
                    <textarea data-testid={`edit-body-${r.id}`} rows={4} value={edit.body}
                      onChange={(e) => setEdit({ ...edit, body: e.target.value })}
                      className="border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-mint" />
                    <p className="text-xs text-slate-400">Resubmitting sends your review back to our team for approval before it goes live again.</p>
                    <div className="flex gap-2">
                      <button data-testid={`resubmit-${r.id}`} onClick={() => saveEdit(r.id)} disabled={busy}
                        className="bg-mint text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-mint-dark disabled:opacity-60">{busy ? "Submitting…" : "Save & resubmit"}</button>
                      <button onClick={() => setEditId(null)} className="text-sm text-slate-500 px-3">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    {r.title && <h4 className="mt-3 font-semibold text-slate-900">{r.title}</h4>}
                    <p className="mt-1 text-slate-600 line-clamp-3">{r.body}</p>
                    {r.status === "rejected" && r.reject_reason && (
                      <p className="mt-2 text-sm text-red-600">Reason: {r.reject_reason}</p>
                    )}
                    <div className="mt-4 flex gap-2 flex-wrap">
                      <button data-testid={`edit-review-${r.id}`} onClick={() => startEdit(r)}
                        className="flex items-center gap-1.5 border border-slate-300 text-slate-700 font-semibold px-4 py-2 rounded-lg hover:bg-slate-50 text-sm"><PenLine className="w-4 h-4" /> Edit review</button>
                      <button data-testid={`delete-review-${r.id}`} onClick={() => deleteReview(r.id)}
                        className="flex items-center gap-1.5 border border-red-300 text-red-700 font-semibold px-4 py-2 rounded-lg hover:bg-red-50 text-sm"><Trash2 className="w-4 h-4" /> Delete</button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>

        <AccountDeletion />
      </div>
    </div>
  );
}
